// RoundRobinDetailPage — Phase T2.0A Prompt 2 member-facing event page.
//
// Same layout blueprint as TournamentDetailPage: flyer → header → info card
// → registration CTA → roster → description → event details → competition
// UI. Uses shared components so the two pages stay visually consistent.

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CaretLeft, Gear, Trophy, User, CalendarBlank, Clock, MapPin } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { useAuth } from '../contexts/AuthContext';
import { roundRobinAPI } from '../lib/roundRobinApi';
import { registrationAPI } from '../lib/registrationApi';

import RRStandingsTable from '../components/roundrobin/RRStandingsTable';
import RRMatchList from '../components/roundrobin/RRMatchList';
import EntryDisplay from '../components/EntryDisplay';
import MemberLink from '../components/MemberLink';
import RegistrationRoster from '../components/registration/RegistrationRoster';
import EventRegistrationDialog from '../components/registration/EventRegistrationDialog';

import EventPhotoGallery from '../components/events/EventPhotoGallery';
import EventHeader from '../components/events/EventHeader';
import EventInfoCard from '../components/events/EventInfoCard';
import RegistrationCTA from '../components/events/RegistrationCTA';
import PaymentSection from '../components/events/PaymentSection';
import PostRegistrationPaymentPrompt from '../components/events/PostRegistrationPaymentPrompt';
import AlternateList from '../components/events/AlternateList';
import { eventIsFull } from '../lib/capacity';
import { fmtEventDate, fmtEventTime } from '../lib/scheduleTime';

// Format entry fee cents → "$25" / "$25.50". Returns "" when unset/zero.
function formatFeeLabel(cents) {
  if (cents == null || cents <= 0) return '';
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}

export default function RoundRobinDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, managedEvents } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isEventManager = Array.isArray(managedEvents) && managedEvents.some(
    (m) => m.event_kind === 'round_robin' && m.event_id === id,
  );
  const canManage = isAdmin || isEventManager;

  const [rr, setRr] = useState(null);
  const [err, setErr] = useState(null);
  const [regOpen, setRegOpen] = useState(false);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  // Post-registration payment prompt orchestration.
  const [payPromptOpen, setPayPromptOpen] = useState(false);
  const [autoStartToken, setAutoStartToken] = useState(0);
  const justRegisteredRef = useRef(false);

  const load = () => roundRobinAPI.get(id).then((r) => setRr(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Load failed'));
  useEffect(() => { load(); }, [id]);

  // Derived — kept above early returns so hooks below always fire.
  // Canonical doubles rule: naming a partner does NOT personally register
  // that partner. A caller is "personally registered" ONLY when their own
  // <slot>_registered_at is populated on some entry.
  const myEntryEarly = (rr?.entries || []).find((e) => {
    if (!user?.id) return false;
    if (e.player1_profile_id === user.id) return !!e.player1_registered_at;
    if (e.player2_profile_id === user.id) return !!e.player2_registered_at;
    return false;
  });
  const isRegisteredEarly = !!myEntryEarly;
  const paidProfileIdsEarly = new Set(
    (rr?.payments || []).filter((p) => p.paid).map((p) => p.profile_id),
  );
  const viewerPaidEarly = !!user?.id && paidProfileIdsEarly.has(user.id);
  const paymentEnabledEarly = !!rr?.payment_type && (rr?.entry_fee_cents || 0) > 0;

  useEffect(() => {
    if (!rr) return;
    if (!justRegisteredRef.current) return;
    if (!isRegisteredEarly) return;
    justRegisteredRef.current = false;
    if (!paymentEnabledEarly || viewerPaidEarly) return;
    setPayPromptOpen(true);
  }, [rr, isRegisteredEarly, paymentEnabledEarly, viewerPaidEarly]);

  if (err) return <div className="text-sm text-red-700" data-testid="rr-detail-error">{err}</div>;
  if (!rr) return <div className="text-sm text-[#6a766e]" data-testid="rr-detail-loading">Loading…</div>;

  const myEntry = myEntryEarly;
  const isRegistered = isRegisteredEarly;
  // Broader entry lookup used for "My Matches" section during
  // competition — a member should see their scheduled matches whether
  // or not their <slot>_registered_at is stamped (defensive).
  const myEntryForMatches = (rr.entries || []).find(
    (e) => e.player1_profile_id === user?.id || e.player2_profile_id === user?.id,
  );
  const isFull = eventIsFull(rr.entries || [], rr.rr_type, rr.max_entries);
  const preCompetition = rr.status === 'registration_open' || rr.status === 'registration_closed';

  const alternates = rr.alternates || [];
  const myAlternate = user?.id
    ? alternates.find((a) => a.profile_id === user.id) : null;
  const isAlternate = !!myAlternate;
  const paidProfileIds = paidProfileIdsEarly;
  const viewerPaid = viewerPaidEarly;
  const paymentEnabled = paymentEnabledEarly;
  const amountLabel = formatFeeLabel(rr.entry_fee_cents);

  const handlePayNow = () => {
    setPayPromptOpen(false);
    if (rr.payment_type === 'venmo' || rr.payment_type === 'zelle') {
      if (rr.payment_link) window.open(rr.payment_link, '_blank', 'noopener,noreferrer');
      return;
    }
    requestAnimationFrame(() => {
      const el = document.getElementById('event-payment-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setAutoStartToken((n) => n + 1);
    });
  };

  const handlePayLater = () => {
    setPayPromptOpen(false);
  };

  async function handleWithdraw() {
    setWithdrawBusy(true);
    try {
      await registrationAPI.rrUnregister(id);
      toast.success('You have been withdrawn.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Could not withdraw.');
    } finally {
      setWithdrawBusy(false);
    }
  }

  const headerActions = canManage && (
    <Link
      to={`/round-robins/${rr.id}/manage`}
      data-testid="rr-manage-btn"
      className="px-3 py-2 rounded-full bg-[#013523] text-white text-sm flex items-center gap-1.5 min-h-[44px]"
    >
      <Gear size={14} /> Manage
    </Link>
  );

  return (
    <div className="space-y-5" data-testid="rr-detail">
      <button
        onClick={() => navigate('/events')}
        className="text-xs text-[#6a766e] flex items-center gap-1"
        data-testid="rr-back"
      >
        <CaretLeft size={14} /> Events
      </button>

      <EventPhotoGallery flyerUrl={rr.flyer_url} eventName={rr.name} kind="round_robin" eventId={rr.id} />

      <EventHeader
        name={rr.name}
        composition={rr.composition || 'open'}
        eventType={rr.rr_type}
        status={rr.status}
        actions={headerActions}
      />

      {(preCompetition || rr.starts_at || rr.location_text || rr.entry_fee_cents != null || rr.match_format_text) && (
        <EventInfoCard event={rr} eventType={rr.rr_type} />
      )}

      {(rr.status === 'registration_open' || rr.status === 'registration_closed') && (
        <RegistrationCTA
          status={rr.status}
          isRegistered={isRegistered}
          isFull={isFull}
          isAlternate={isAlternate}
          alternatePosition={myAlternate?.position || null}
          onOpenRegistration={() => setRegOpen(true)}
          onWithdraw={handleWithdraw}
          withdrawBusy={withdrawBusy}
          eventKind="round_robin"
          eventId={rr.id}
          onReload={load}
          viewerPaid={!!user?.id && paidProfileIds.has(user.id)}
        />
      )}

      {preCompetition && rr.rr_type === 'doubles' && (
        <RegistrationRoster
          entries={rr.entries}
          title="Registered Players"
          maxEntries={rr.max_entries || null}
          eventType="doubles"
          paidProfileIds={paidProfileIds}
        />
      )}
      {preCompetition && rr.rr_type === 'singles' && (
        <RRSinglesRoster entries={rr.entries} paidProfileIds={paidProfileIds} />
      )}

      {preCompetition && alternates.length > 0 && (
        <AlternateList alternates={alternates} viewerProfileId={user?.id} />
      )}

      {rr.description && (
        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="event-description">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-2">About this event</div>
          <div className="text-[14px] text-[#2a332c] whitespace-pre-line leading-relaxed">
            {rr.description}
          </div>
        </div>
      )}

      {rr.match_format_text && (
        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="event-details">
          <div className="text-[11px] uppercase tracking-wider text-[#6a766e] mb-2">Event Details</div>
          <div className="text-[14px] text-[#013523] whitespace-pre-line">
            {rr.match_format_text}
          </div>
        </div>
      )}

      {preCompetition && (
        <PaymentSection
          event={rr}
          eventKind="round_robin"
          viewer={user}
          viewerRegistered={isRegistered}
          viewerPaid={viewerPaid}
          onPaid={load}
          autoStartToken={autoStartToken}
        />
      )}

      {rr.champion && (
        <div className="border border-[#c9a93a] rounded-2xl bg-gradient-to-r from-[#fffbf0] to-[#fff7e0] px-4 py-3 flex items-center gap-3" data-testid="rr-header-champion">
          <Trophy size={24} weight="fill" className="text-[#c9a93a]" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#c9a93a] font-semibold">Champion</div>
            <div className="text-base text-[#013523] font-semibold"><EntryDisplay entry={rr.champion} /></div>
            {rr.runner_up && <div className="text-[11px] text-[#6a766e]">Runner-up: <EntryDisplay entry={rr.runner_up} /></div>}
          </div>
        </div>
      )}

      {rr.matches.length > 0 && (
        <>
          {user?.id && myEntryForMatches && (
            <MyMatchesSection rr={rr} myEntryId={myEntryForMatches.id} />
          )}
          <RRStandingsTable rr={rr} />
          <RRMatchList rr={rr} rrId={id} onChanged={load} admin={false} />
        </>
      )}

      {rr.schedule_hidden_until_published && !preCompetition && (
        <div className="border border-dashed border-[#e3dcd1] rounded-2xl bg-[#fbfaf8] p-6 text-center" data-testid="rr-schedule-hidden">
          <div className="text-sm font-medium text-[#013523]">Schedule is being finalized</div>
          <div className="text-[12px] text-[#6a766e] mt-1">
            The tournament director is still arranging courts and start times. You&apos;ll see your matches here as soon as the schedule is published.
          </div>
        </div>
      )}

      {rr.matches.length === 0 && !preCompetition && !rr.schedule_hidden_until_published && (
        <div className="border border-dashed border-[#e3dcd1] rounded-2xl p-8 text-center text-sm text-[#6a766e]" data-testid="rr-no-schedule">
          Schedule has not been generated yet.
        </div>
      )}

      <EventRegistrationDialog
        open={regOpen}
        onOpenChange={setRegOpen}
        eventKind="round_robin"
        eventId={rr.id}
        eventName={rr.name}
        eventType={rr.rr_type}
        composition={rr.composition || 'open'}
        onRegistered={() => {
          justRegisteredRef.current = true;
          load();
        }}
      />

      <PostRegistrationPaymentPrompt
        open={payPromptOpen}
        onOpenChange={setPayPromptOpen}
        eventTypeLabel="Round Robin"
        amountLabel={amountLabel}
        paymentMethod={rr.payment_type}
        onPayNow={handlePayNow}
        onPayLater={handlePayLater}
      />
    </div>
  );
}

function OpponentNames({ entry }) {
  if (!entry) return <span className="italic text-[#9aa39d]">TBD</span>;
  const p1 = entry.player1_name, p2 = entry.player2_name;
  const id1 = entry.player1_profile_id, id2 = entry.player2_profile_id;
  // Show both players cleanly on their own line (no duplicated parenthetical).
  if (p1 || p2) {
    return (
      <div className="space-y-0.5">
        {p1 && <div className="leading-tight"><MemberLink profileId={id1} name={p1} /></div>}
        {p2 && <div className="leading-tight"><MemberLink profileId={id2} name={p2} /></div>}
      </div>
    );
  }
  return <span>{entry.team_name || entry.display_name || 'Player'}</span>;
}

function MyMatchesSection({ rr, myEntryId }) {
  const labels = rr.pool_labels || {};
  const poolLabel = (pn) => labels[String(pn)] || `Pool ${pn}`;
  const mine = rr.matches
    .filter((m) => (m.entry1?.id === myEntryId || m.entry2?.id === myEntryId))
    .sort((a, b) => {
      const ta = a.scheduled_at || '';
      const tb = b.scheduled_at || '';
      if (ta && tb) return ta < tb ? -1 : ta > tb ? 1 : 0;
      if (ta) return -1;
      if (tb) return 1;
      return 0;
    });
  if (mine.length === 0) return null;
  const finalLabel = (m) => (rr.championship_format === 'per_pool' ? `${poolLabel(m.pool_number)} Final` : 'Final');
  return (
    <div className="border border-emerald-200 rounded-2xl bg-emerald-50/40 p-4 sm:p-5" data-testid="rr-my-matches">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-emerald-900 font-semibold mb-3">
        <User size={12} weight="duotone" /> My Matches
      </div>
      <ul className="space-y-3">
        {mine.map((m) => {
          const opp = m.entry1?.id === myEntryId ? m.entry2 : m.entry1;
          const when = m.scheduled_at ? new Date(m.scheduled_at) : null;
          return (
            <li key={m.id} className="rounded-xl bg-white border border-emerald-100 p-3.5 sm:p-4" data-testid={`rr-my-match-${m.id}`}>
              {/* PRIMARY — who I'm playing (full card width) */}
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#9aa39d] font-semibold">vs</div>
              <div className="mt-1 text-lg sm:text-xl font-semibold text-[#013523] break-words" data-testid={`rr-my-match-${m.id}-opponent`}>
                <OpponentNames entry={opp} />
              </div>
              {/* META — division • round */}
              <div className="mt-3 flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-900 text-emerald-50 font-semibold tracking-wide uppercase">
                  {m.is_final ? finalLabel(m) : poolLabel(m.pool_number)}
                </span>
                {m.round_number != null && !m.is_final && (
                  <span className="text-[11px] uppercase tracking-wide text-[#6a766e] font-medium">Round {m.round_number}</span>
                )}
                {m.status === 'completed' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Done</span>
                )}
              </div>
              {/* META — date • time • court */}
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-[#6a766e]">
                {when && (
                  <span className="inline-flex items-center gap-1.5" data-testid={`rr-my-match-${m.id}-date`}>
                    <CalendarBlank size={13} weight="duotone" className="text-[#013523]" /> {fmtEventDate(m.scheduled_at)}
                  </span>
                )}
                {when && (
                  <span className="inline-flex items-center gap-1.5" data-testid={`rr-my-match-${m.id}-time`}>
                    <Clock size={13} weight="duotone" className="text-[#013523]" /> {fmtEventTime(m.scheduled_at)}
                  </span>
                )}
                {m.court_label && (
                  <span className="inline-flex items-center gap-1.5" data-testid={`rr-my-match-${m.id}-court`}>
                    <MapPin size={13} weight="duotone" className="text-[#013523]" /> Court {m.court_label}
                  </span>
                )}
                {!when && !m.court_label && <span className="text-[#c1bcb1]">Time & court TBD</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RRSinglesRoster({ entries = [], paidProfileIds = new Set() }) {
  const rows = entries
    .filter((e) => e.player1_profile_id)
    .map((e) => ({ id: e.id, profileId: e.player1_profile_id, name: e.player1_name || e.display_name }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 sm:p-5" data-testid="singles-roster">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-[#6a766e]">Registered Players</div>
        <div className="text-[11.5px] text-[#6a766e]" data-testid="singles-roster-count">
          {rows.length} Player{rows.length === 1 ? '' : 's'} Registered
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-[#6a766e]" data-testid="singles-roster-empty">
          Nobody has registered yet — be the first!
        </div>
      ) : (
        <ul className="divide-y divide-[#f3f1ec]">
          {rows.map((r) => (
            <li key={r.id} className="py-2 text-[14px] text-[#013523] font-medium flex items-center gap-2" data-testid={`singles-roster-row-${r.profileId}`}>
              <MemberLink profileId={r.profileId} name={r.name} />
              {paidProfileIds.has(r.profileId) && (
                <span
                  data-testid={`singles-roster-paid-${r.profileId}`}
                  title="Paid"
                  className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5"
                >
                  ✓ Paid
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
